import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import ReactSelect from '@components/Select';
import AgGrid from '@components/AgGrid';
import {
  buildOrderLogSummary,
  getOrderLogsForDateRange,
  sortOrderLogsNewestFirst,
} from '@utilities/orderLogging';
import type { OrderLogEntry } from '@src/types/orderLogging';
import { orderLoggingColDefs, toOrderLogGridRow, type OrderLogGridRow } from './colDefs';

import 'react-datepicker/dist/react-datepicker.css';
import '../Report/Report.scss';

const ALL_KINDS = { label: 'All (Food + Room)', value: '' };
const KIND_FOOD = { label: 'Food orders', value: 'food' };
const KIND_ROOM = { label: 'Room bills', value: 'room' };
const KIND_OPTIONS = [ALL_KINDS, KIND_FOOD, KIND_ROOM];

const ALL_STATUS = { label: 'All statuses', value: '' };
const STATUS_OK = { label: 'Success', value: 'success' };
const STATUS_FAIL = { label: 'Failed', value: 'failed' };
const STATUS_OPTIONS = [ALL_STATUS, STATUS_OK, STATUS_FAIL];

const ALL_ACTIONS = { label: 'All actions', value: '' };
const ACTION_UPDATE = { label: 'Updates', value: 'update' };
const ACTION_DELETE = { label: 'Deletes', value: 'delete' };
const ACTION_OPTIONS = [ALL_ACTIONS, ACTION_UPDATE, ACTION_DELETE];

function SummaryChip({ label, value }: { label: string; value: string | number }) {
  return (
    <Paper
      elevation={0}
      sx={{
        px: 2,
        py: 1.5,
        minWidth: 120,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Paper>
  );
}

type ExportRow = {
  'Date / Time': string;
  Kind: string;
  Action: string;
  Status: string;
  'Table / Guest': string;
  'Order ID': string;
  'UO / Seq': string;
  'Prev Total': string | number;
  'Next Total': string | number;
  'Deleted by': string;
  Source: string;
  Device: string;
  'User Agent': string;
  Platform: string;
  Timezone: string;
  'Change Summary': string;
  'Previous JSON': string;
  'Next JSON': string;
  Error: string;
};

const buildExportRows = (rows: OrderLogGridRow[]): ExportRow[] =>
  rows.map((row) => ({
    'Date / Time': row.deletedAtMs
      ? moment(row.deletedAtMs).local().format('DD MMM YYYY, h:mm:ss a')
      : '',
    Kind: row.orderKind || '',
    Action: row.action || '',
    Status: row.status || '',
    'Table / Guest': row.tableOrGuest || '',
    'Order ID': row.orderId || '',
    'UO / Seq': String(row.snapshot?.orderSeqNo || row.snapshot?.uoId || ''),
    'Prev Total': row.previous?.totalCost ?? '',
    'Next Total': row.next?.totalCost ?? row.snapshot?.totalCost ?? '',
    'Deleted by': row.deletedByLabel || '',
    Source: row.source || '',
    Device: row.deviceSummary || '',
    'User Agent': row.device?.userAgent || '',
    Platform: row.device?.platform || '',
    Timezone: row.device?.timezone || '',
    'Change Summary': row.changeSummary || '',
    'Previous JSON': row.previous ? JSON.stringify(row.previous.raw || row.previous) : '',
    'Next JSON': row.next ? JSON.stringify(row.next.raw || row.next) : '',
    Error: row.errorMessage || '',
  }));

const getExportFilename = (
  restaurantName: string,
  startDate: Date,
  endDate: Date
): string => {
  const rest = (restaurantName || 'restaurant').replace(/[^\w-]+/g, '_').slice(0, 40);
  return `[OrderLog]${rest}_${moment(startDate).format('D-MMM-YYYY')}_${moment(endDate).format('D-MMM-YYYY')}`;
};

const downloadExcelFromRows = (rows: ExportRow[], filename: string) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]) as Array<keyof ExportRow>;
  const escapeCell = (value: string | number) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const headerHtml = headers.map((h) => `<th>${escapeCell(h)}</th>`).join('');
  const bodyHtml = rows
    .map(
      (row) =>
        `<tr>${headers.map((h) => `<td>${escapeCell(row[h])}</td>`).join('')}</tr>`
    )
    .join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8" /></head><body><table border="1"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const OrderLoggingReport = ({
  isSuperAdmin,
  selectedRestId,
  restaurantName,
}: {
  isSuperAdmin: boolean;
  selectedRestId: string;
  restaurantName: string;
}) => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const restId = restaurantId || selectedRestId;

  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayEnd = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  const [startDate, setStartDate] = useState(todayStart);
  const [endDate, setEndDate] = useState(todayEnd);
  const [kindFilter, setKindFilter] = useState(ALL_KINDS);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUS);
  const [actionFilter, setActionFilter] = useState(ALL_ACTIONS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<OrderLogEntry[]>([]);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate(restId ? `/admin/${restId}/orders` : '/admin', { replace: true });
    }
  }, [isSuperAdmin, navigate, restId]);

  const loadLogs = useCallback(async () => {
    if (!restId || !isSuperAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const startMs = moment(startDate).startOf('day').valueOf();
      const endMs = moment(endDate).endOf('day').valueOf();
      const data = await getOrderLogsForDateRange(restId, startMs, endMs);
      setEntries(data);
      setKindFilter(ALL_KINDS);
      setStatusFilter(ALL_STATUS);
      setActionFilter(ALL_ACTIONS);
    } catch (err) {
      console.error(err);
      setEntries([]);
      setError(
        (err as Error)?.message ||
          'Failed to load order logs. Create the Firestore composite index when prompted.'
      );
    } finally {
      setLoading(false);
    }
  }, [restId, startDate, endDate, isSuperAdmin]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  const filteredEntries = useMemo(() => {
    let rows = entries;
    if (kindFilter.value) {
      rows = rows.filter((e) => e.orderKind === kindFilter.value);
    }
    if (statusFilter.value) {
      rows = rows.filter((e) => e.status === statusFilter.value);
    }
    if (actionFilter.value) {
      rows = rows.filter((e) => e.action === actionFilter.value);
    }
    return sortOrderLogsNewestFirst(rows);
  }, [entries, kindFilter, statusFilter, actionFilter]);

  const summary = useMemo(() => buildOrderLogSummary(filteredEntries), [filteredEntries]);
  const rowData = useMemo(() => filteredEntries.map(toOrderLogGridRow), [filteredEntries]);

  const exportFilename = useMemo(
    () => getExportFilename(restaurantName || restId, startDate, endDate),
    [restaurantName, restId, startDate, endDate]
  );

  const handleDownloadCsv = useCallback(() => {
    const rows = buildExportRows(rowData);
    if (!rows.length) return;
    const csvExporter = new ExportToCsv({
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: 'Order Logging Report',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      filename: exportFilename,
    });
    csvExporter.generateCsv(rows);
  }, [rowData, exportFilename]);

  const handleDownloadExcel = useCallback(() => {
    downloadExcelFromRows(buildExportRows(rowData), exportFilename);
  }, [rowData, exportFilename]);

  const pinnedTopRowData = useMemo(
    () => [
      {
        id: 'Total:',
        isTotalRow: true,
        restaurantId: '',
        orderId: '',
        collectionName: 'orders',
        orderKind: 'food',
        action: 'delete',
        status: 'success',
        source: '',
        errorMessage: null,
        snapshot: { totalCost: summary.total },
        previous: null,
        next: null,
        deletedBy: { uid: '', phone: '', name: '' },
        device: {} as any,
        localDate: '',
        deletedAtMs: 0,
        clientRequestId: '',
        deletedByLabel: '',
        tableOrGuest: '',
        deviceSummary: '',
        snapshotSummary: '',
        changeSummary: '',
      },
    ],
    [summary.total]
  );

  if (!isSuperAdmin) {
    return null;
  }

  const canDownload = !loading && rowData.length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3, minHeight: 'calc(100vh - 160px)' }} className="mobReportWrap">
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          Order Logging Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {restaurantName || restId} · food & room delete/update events (prev → next)
        </Typography>
      </Box>

      <div className="filterRow filterRow--secondary reportFilters" style={{ marginBottom: 20 }}>
        <div className="filterBlock filterBlock--inline filterBlock--dates">
          <div className="filterLabel">Date Range (From - To)</div>
          <div className="dateRange">
            <div className="dateField">
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => date && setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="d MMM yyyy"
                placeholderText="Start date"
              />
            </div>
            <div className="dateField">
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => {
                  if (date) {
                    const end = new Date(date);
                    end.setHours(23, 59, 59, 999);
                    setEndDate(end);
                  }
                }}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="d MMM yyyy"
                placeholderText="End date"
              />
            </div>
          </div>
        </div>

        <div className="filterBlock filterBlock--inline filterBlock--service">
          <div className="filterLabel">Order Kind</div>
          <ReactSelect
            className="filterTypes"
            value={kindFilter}
            options={KIND_OPTIONS}
            onChange={(opt: any) => setKindFilter(opt || ALL_KINDS)}
          />
        </div>

        <div className="filterBlock filterBlock--inline filterBlock--payment">
          <div className="filterLabel">Status</div>
          <ReactSelect
            className="filterTypes"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={(opt: any) => setStatusFilter(opt || ALL_STATUS)}
          />
        </div>

        <div className="filterBlock filterBlock--inline filterBlock--service">
          <div className="filterLabel">Action</div>
          <ReactSelect
            className="filterTypes"
            value={actionFilter}
            options={ACTION_OPTIONS}
            onChange={(opt: any) => setActionFilter(opt || ALL_ACTIONS)}
          />
        </div>
      </div>

      <div className="filterRow filterRow--primary actionButtons" style={{ marginBottom: 16 }}>
        <div className="filterBlock">
          <div className="totalText">Total events: [ {summary.total} ]</div>
        </div>
        <div className="filterBlock filterBlock--right">
          <ButtonGroup variant="outlined" color="primary" className="actionButtons__group">
            <Button className="btnReport" disabled={!canDownload} onClick={handleDownloadCsv}>
              D-CSV
            </Button>
            <Button className="btnReport" disabled={!canDownload} onClick={handleDownloadExcel}>
              D-XLS
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
        <SummaryChip label="Total" value={summary.total} />
        <SummaryChip label="Success" value={summary.successCount} />
        <SummaryChip label="Failed" value={summary.failedCount} />
        <SummaryChip label="Food" value={summary.foodCount} />
        <SummaryChip label="Room" value={summary.roomCount} />
        <SummaryChip label="Updates" value={summary.updateCount} />
        <SummaryChip label="Deletes" value={summary.deleteCount} />
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="secondary" />
        </Box>
      )}

      {!loading && error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && filteredEntries.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
          <Typography color="text.secondary">
            No order delete events for{' '}
            {moment(startDate).format('DD MMM YYYY')}
            {moment(startDate).format('YYYY-MM-DD') !== moment(endDate).format('YYYY-MM-DD')
              ? ` – ${moment(endDate).format('DD MMM YYYY')}`
              : ''}
            .
          </Typography>
        </Paper>
      )}

      {!loading && filteredEntries.length > 0 && (
        <AgGrid
          rowData={rowData}
          colDefs={orderLoggingColDefs}
          pinnedTopRowData={pinnedTopRowData}
          defaultColDef={{
            resizable: true,
            sortable: false,
          }}
          initialState={{
            sort: {
              sortModel: [{ colId: 'deletedAtMs', sort: 'desc' }],
            },
          }}
          tooltipShowDelay={400}
          suppressRowClickSelection
          suppressCellFocus
        />
      )}
    </Container>
  );
};

const mapStateToProps = (state: any) => {
  const { user, common } = state;
  return {
    isSuperAdmin: !!(user?.user?.isSuperAdmin),
    selectedRestId: common?.selectedRestaurant?.docId || '',
    restaurantName: common?.selectedRestaurant?.name || '',
  };
};

export default connect(mapStateToProps)(OrderLoggingReport);
